import React, { useState, useEffect } from 'react';
import AIInsights from './AIInsights';
import AnalyticsDetailModal from './AnalyticsDetailModal';
import { useEventCards } from '../hooks/useEventCards';

const DetailedAnalytics = ({ data, formatNumber, errors, hasAPIKeys }) => {
  const [timeFilters, setTimeFilters] = useState({
    facebook: '7d',
    instagram: '7d',
    google: '7d',
    cloudflare: '7d'
  });
  const [configStatus, setConfigStatus] = useState({});
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  // Use the event cards hook for tilt effects
  useEventCards();

  // Fetch configuration status for each platform
  useEffect(() => {
    const fetchConfigStatus = async () => {
      try {
        const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/config/status');
        if (response.ok) {
          const data = await response.json();
          setConfigStatus(data.config || {});
        }
      } catch (error) {
        console.error('Failed to fetch configuration status:', error);
      }
    };
    fetchConfigStatus();
  }, []);

  const handleTimeFilterChange = (platform, value) => {
    setTimeFilters(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const handleViewDetails = (platform) => {
    setSelectedPlatform(platform);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPlatform(null);
  };

  // Only show analytics for configured platforms
  const configuredAnalyticsCards = [
    {
      key: 'facebook',
      title: 'Facebook Pages Analytics',
      isConfigured: configStatus.facebook?.configured || false,
      metrics: configStatus.facebook?.configured ? [
        { icon: 'fas fa-users', value: formatNumber(data?.facebook?.pageLikes || 0), label: 'Page Likes', change: data?.facebook?.trend || '+0%', positive: true },
        { icon: 'fas fa-eye', value: formatNumber(data?.facebook?.postReach || 0), label: 'Post Reach', change: data?.facebook?.trend || '+0%', positive: true },
        { icon: 'fas fa-heart', value: formatNumber(data?.facebook?.postEngagement || 0), label: 'Post Engagement', change: data?.facebook?.trend || '+0%', positive: true },
        { icon: 'fas fa-share', value: formatNumber(data?.facebook?.shares || 0), label: 'Shares', change: data?.facebook?.trend || '+0%', positive: true }
      ] : []
    },
    {
      key: 'instagram',
      title: 'Instagram Analytics',
      isConfigured: configStatus.instagram?.configured || false,
      metrics: configStatus.instagram?.configured ? [
        { icon: 'fas fa-users', value: formatNumber(data?.instagram?.followers || 0), label: 'Followers', change: data?.instagram?.trend || '+0%', positive: true },
        { icon: 'fas fa-eye', value: formatNumber(data?.instagram?.impressions || 0), label: 'Impressions', change: data?.instagram?.trend || '+0%', positive: true },
        { icon: 'fas fa-heart', value: formatNumber(data?.instagram?.likes || 0), label: 'Likes', change: data?.instagram?.trend || '+0%', positive: true },
        { icon: 'fas fa-comment', value: formatNumber(data?.instagram?.comments || 0), label: 'Comments', change: data?.instagram?.trend || '+0%', positive: true }
      ] : []
    },
    {
      key: 'google',
      title: 'Google Analytics',
      isConfigured: configStatus.google?.configured || false,
      metrics: configStatus.google?.configured ? [
        { icon: 'fas fa-chart-line', value: formatNumber(data?.google?.sessions || 0), label: 'Sessions', change: data?.google?.trend || '+0%', positive: true },
        { icon: 'fas fa-eye', value: formatNumber(data?.google?.pageViews || 0), label: 'Page Views', change: data?.google?.trend || '+0%', positive: true },
        { icon: 'fas fa-clock', value: (data?.google?.avgSessionDuration || 0) + 'm', label: 'Avg. Session Duration', change: data?.google?.trend || '+0%', positive: true },
        { icon: 'fas fa-percentage', value: (data?.google?.bounceRate || 0) + '%', label: 'Bounce Rate', change: data?.google?.trend || '+0%', positive: false }
      ] : []
    },
    {
      key: 'cloudflare',
      title: 'Cloudflare Analytics',
      isConfigured: configStatus.cloudflare?.configured || false,
      metrics: configStatus.cloudflare?.configured ? [
        { icon: 'fas fa-globe', value: formatNumber(data?.cloudflare?.requests || 0), label: 'Requests', change: data?.cloudflare?.trend || '+0%', positive: true },
        { icon: 'fas fa-download', value: formatNumber(data?.cloudflare?.bandwidth || 0), label: 'Bandwidth', change: data?.cloudflare?.trend || '+0%', positive: true },
        { icon: 'fas fa-shield-alt', value: formatNumber(data?.cloudflare?.threats || 0), label: 'Threats Blocked', change: data?.cloudflare?.trend || '+0%', positive: true },
        { icon: 'fas fa-percentage', value: (data?.cloudflare?.uptime || 0) + '%', label: 'Uptime', change: data?.cloudflare?.trend || '+0%', positive: true }
      ] : []
    }
  ].filter(card => card.isConfigured); // Only show configured platforms

  // If no platforms are configured, show a message
  if (configuredAnalyticsCards.length === 0) {
    return (
      <section className="detailed-analytics">
        <div className="container">
          <h2>ANALYSES DÉTAILLÉES</h2>
          <div className="no-analytics-message">
            <div className="no-analytics-icon">📊</div>
            <h3>Aucune analyse disponible</h3>
            <p>Configurez au moins une API pour voir les analyses détaillées de vos plateformes.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="detailed-analytics">
      <div className="container">
        <h2>ANALYSES DÉTAILLÉES</h2>
        <div className="analytics-grid">
          {configuredAnalyticsCards.map((card) => (
            <div key={card.key} className="event-card analytics-card">
              <div className="analytics-header">
                <h3>{card.title}</h3>
                <div className="time-filter">
                  <select 
                    value={timeFilters[card.key]} 
                    onChange={(e) => handleTimeFilterChange(card.key, e.target.value)}
                    className="time-select"
                  >
                    <option value="1d">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="90d">Last 90 Days</option>
                  </select>
                </div>
              </div>
              
              <div className="analytics-metrics">
                {card.metrics.map((metric, index) => (
                  <div key={index} className="metric-card">
                    <div className="metric-icon">
                      <i className={metric.icon}></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-value">{metric.value}</div>
                      <div className="metric-label">{metric.label}</div>
                      <div className={`metric-change ${metric.positive ? 'positive' : 'negative'}`}>
                        {metric.change}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="analytics-insights">
                <AIInsights 
                  platform={card.key} 
                  data={data[card.key]} 
                  timeRange={timeFilters[card.key]}
                />
              </div>
              
              <div className="analytics-actions">
                <button 
                  className="btn-detail"
                  onClick={() => handleViewDetails(card.key)}
                >
                  <i className="fas fa-eye"></i>
                  Voir détail
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Analytics Detail Modal */}
      <AnalyticsDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
        platform={selectedPlatform}
        data={selectedPlatform ? data[selectedPlatform] : null}
        timeRange={selectedPlatform ? timeFilters[selectedPlatform] : '7d'}
      />
    </section>
  );
};

export default DetailedAnalytics;